import type { CheckoutSession } from '@/lib/types';

function fmtCurrency(value: string | number, currency: string): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(typeof value === 'string' ? parseFloat(value) : value);
}

interface Props {
  session: CheckoutSession;
}

export function CheckoutHeader({ session }: Props) {
  const logoUrl = (session.ui_config?.logo_url as string | undefined) ?? null;
  const hasWallet = !!session.payable_amount && !!session.wallet_applied_amount;
  const displayAmount = hasWallet ? session.payable_amount! : session.amount;

  const productName = session.metadata?.product_name
    ? String(session.metadata.product_name)
    : session.order_reference
      ? `Pedido ${session.order_reference}`
      : 'Pago seguro';

  const productDescription = session.metadata?.product_description
    ? String(session.metadata.product_description)
    : null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-4">
      {/* Merchant identity */}
      {(logoUrl || session.merchant_display_name) && (
        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
          {logoUrl && (
            <img
              src={logoUrl}
              alt={session.merchant_display_name ?? 'Comercio'}
              className="w-10 h-10 rounded-lg object-contain border border-gray-100 flex-shrink-0"
            />
          )}
          {session.merchant_display_name && (
            <p className="text-sm font-semibold text-gray-700 truncate">
              {session.merchant_display_name}
            </p>
          )}
        </div>
      )}

      {/* Product info */}
      <p className="text-sm font-medium text-gray-800 mb-0.5">{productName}</p>
      {productDescription && (
        <p className="text-xs text-gray-400 mb-3 line-clamp-2">{productDescription}</p>
      )}

      {/* Amount */}
      <div className="mt-2">
        <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Total</p>
        <p className="text-3xl font-bold text-gray-900 leading-tight">
          {fmtCurrency(displayAmount, session.currency)}
        </p>
        {hasWallet && (
          <p className="text-sm text-green-600 mt-1">
            Descuento aplicado: −{fmtCurrency(session.wallet_applied_amount!, session.currency)}
          </p>
        )}
      </div>
    </div>
  );
}
